import React, { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/components/AuthProvider';
import { useTransactions } from '@/hooks/useTransactions';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  DollarSign, 
  TrendingUp, 
  Gavel, 
  Plus,
  Eye,
  Edit,
  Calendar,
  BarChart3,
  Target,
  Wallet
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

const ProjectSalesRevenue = () => {
  const { user } = useAuth();
  const { transactions, loading, getUserTransactions } = useTransactions();
  const { toast } = useToast();
  const [listings, setListings] = useState([]);
  const [revenueData, setRevenueData] = useState([]);

  // Mock data for demonstration
  const mockListings = [
    {
      id: 1,
      projectName: "Solar Farm Maharashtra",
      creditsListed: 50000,
      creditsSold: 30000,
      pricePerCredit: 850,
      totalRevenue: 25500000,
      status: "Active",
      listingType: "Auction",
      createdDate: "2024-12-15"
    },
    {
      id: 2,
      projectName: "Wind Power Tamil Nadu",
      creditsListed: 75000,
      creditsSold: 45000,
      pricePerCredit: 920,
      totalRevenue: 41400000,
      status: "Active",
      listingType: "Fixed Price",
      createdDate: "2024-12-10"
    },
    {
      id: 3,
      projectName: "Biogas Karnataka",
      creditsListed: 30000,
      creditsSold: 30000,
      pricePerCredit: 780,
      totalRevenue: 23400000,
      status: "Completed",
      listingType: "Auction",
      createdDate: "2024-11-20"
    }
  ];

  const mockRevenueData = [
    { month: 'Jul', revenue: 1.2, volume: 15000 },
    { month: 'Aug', revenue: 1.8, volume: 22000 },
    { month: 'Sep', revenue: 2.3, volume: 28000 },
    { month: 'Oct', revenue: 2.1, volume: 25000 },
    { month: 'Nov', revenue: 2.9, volume: 35000 },
    { month: 'Dec', revenue: 3.4, volume: 42000 }
  ];

  const salesByType = [
    { name: 'Auction Sales', value: 65, color: '#3B82F6' },
    { name: 'Fixed Price', value: 25, color: '#10B981' },
    { name: 'Negotiated', value: 10, color: '#F59E0B' }
  ];

  useEffect(() => {
    if (user?.id) {
      getUserTransactions(user.id);
    }
  }, [user?.id]);

  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'Active': { color: 'bg-green-100 text-green-800' },
      'Completed': { color: 'bg-blue-100 text-blue-800' },
      'Paused': { color: 'bg-yellow-100 text-yellow-800' },
      'Draft': { color: 'bg-gray-100 text-gray-800' }
    };
    
    const config = statusMap[status as keyof typeof statusMap] || statusMap.Draft;
    
    return (
      <Badge className={config.color}>
        {status}
      </Badge>
    );
  };

  const totalRevenue = mockListings.reduce((sum, listing) => sum + listing.totalRevenue, 0);
  const totalCreditsSold = mockListings.reduce((sum, listing) => sum + listing.creditsSold, 0);
  const avgPrice = totalCreditsSold > 0 ? totalRevenue / totalCreditsSold : 0;

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Sales & Revenue</h1>
              <p className="text-muted-foreground">
                Manage your carbon credit sales and track revenue performance
              </p>
            </div>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create New Listing
            </Button>
          </div>
        </div>

        {/* Revenue Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(totalRevenue)}
              </div>
              <div className="text-sm text-muted-foreground">+23% from last month</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Credits Sold</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {totalCreditsSold.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">tCO₂e this month</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Average Price</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                ₹{Math.round(avgPrice)}
              </div>
              <div className="text-sm text-muted-foreground">per credit</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Listings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {mockListings.filter(l => l.status === 'Active').length}
              </div>
              <div className="text-sm text-muted-foreground">ongoing sales</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="listings">Listing Management</TabsTrigger>
            <TabsTrigger value="auctions">Auction Creation</TabsTrigger>
            <TabsTrigger value="analytics">Sales Analytics</TabsTrigger>
            <TabsTrigger value="revenue">Revenue Tracking</TabsTrigger>
          </TabsList>

          <TabsContent value="listings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  My Listings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {mockListings.map((listing) => (
                    <Card key={listing.id} className="border hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold">{listing.projectName}</h3>
                              {getStatusBadge(listing.status)}
                              <Badge variant="outline">{listing.listingType}</Badge>
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Credits Listed:</span>
                                <div className="font-semibold">{listing.creditsListed.toLocaleString()}</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Credits Sold:</span>
                                <div className="font-semibold text-green-600">{listing.creditsSold.toLocaleString()}</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Price per Credit:</span>
                                <div className="font-semibold">₹{listing.pricePerCredit}</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Total Revenue:</span>
                                <div className="font-semibold text-primary">{formatCurrency(listing.totalRevenue)}</div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </Button>
                            <Button variant="outline" size="sm">
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                            {listing.status === 'Active' && (
                              <Button variant="outline" size="sm">
                                <Calendar className="w-4 h-4 mr-2" />
                                Extend
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="auctions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gavel className="w-5 h-5" />
                  Create Auction
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-w-2xl mx-auto text-center py-12">
                  <Gavel className="w-16 h-16 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Set Up Carbon Credit Auction</h3>
                  <p className="text-muted-foreground mb-6">
                    Create competitive auctions to maximize revenue from your carbon credits
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <Card className="p-4 border-2 border-dashed hover:border-primary cursor-pointer">
                      <div className="text-center">
                        <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                        <h4 className="font-semibold">Standard Auction</h4>
                        <p className="text-sm text-muted-foreground">Open bidding format</p>
                      </div>
                    </Card>
                    <Card className="p-4 border-2 border-dashed hover:border-primary cursor-pointer">
                      <div className="text-center">
                        <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
                        <h4 className="font-semibold">Reserve Auction</h4>
                        <p className="text-sm text-muted-foreground">Set minimum price</p>
                      </div>
                    </Card>
                  </div>
                  <Button size="lg">
                    <Plus className="w-5 h-5 mr-2" />
                    Start Auction Setup
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Revenue Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={mockRevenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#3B82F6" 
                        fill="#3B82F6" 
                        fillOpacity={0.1}
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Sales by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={salesByType}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}%`}
                      >
                        {salesByType.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Volume vs Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={mockRevenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Bar yAxisId="left" dataKey="volume" fill="#10B981" name="Volume (tCO₂e)" />
                    <Bar yAxisId="right" dataKey="revenue" fill="#3B82F6" name="Revenue (Cr)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="revenue" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Revenue Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-green-800">This Month</span>
                        <TrendingUp className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="text-2xl font-bold text-green-700">{formatCurrency(34500000)}</div>
                      <div className="text-sm text-green-600">+23% from last month</div>
                    </div>
                    
                    <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-blue-800">This Quarter</span>
                        <BarChart3 className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="text-2xl font-bold text-blue-700">{formatCurrency(98500000)}</div>
                      <div className="text-sm text-blue-600">Q4 2024 performance</div>
                    </div>
                    
                    <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-purple-800">This Year</span>
                        <DollarSign className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="text-2xl font-bold text-purple-700">{formatCurrency(287500000)}</div>
                      <div className="text-sm text-purple-600">2024 total revenue</div>
                    </div>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue Breakdown by Project</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {mockListings.map((listing) => (
                          <div key={listing.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div>
                              <h4 className="font-semibold">{listing.projectName}</h4>
                              <p className="text-sm text-muted-foreground">
                                {listing.creditsSold.toLocaleString()} credits sold
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-primary">
                                {formatCurrency(listing.totalRevenue)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                ₹{listing.pricePerCredit}/credit
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default ProjectSalesRevenue;